import {
  deviceTypes,
  deviceExecutions,
  deviceHostings,
  deviceFormFactors,
} from './device-classification';
import { SearchSelect } from './SearchSelect';
import {
  deviceClassificationRules,
  deviceClassificationOptions,
  normalizeDeviceClassification,
  type DeviceCreate,
  type DeviceDto,
} from '@onig-control/contracts';
import { type FormEvent, useEffect, useState } from 'react';
import { api } from './api';
import { ErrorMessage } from './components';
import type { Company, Contact, Site } from './types';

function classificationResetFields(input: Parameters<typeof normalizeDeviceClassification>[0]) {
  const next = normalizeDeviceClassification(input);
  const labels = {
    execution: 'Ausführung',
    hosting: 'Betriebsort',
    formFactor: 'Bauform',
    roles: 'Host-Rolle',
    hostingProvider: 'Anbieter',
    hostingRegion: 'Region / Rechenzentrum',
  };
  return (Object.keys(labels) as (keyof typeof labels)[])
    .filter((key) => {
      const value = input[key];
      return (
        value != null &&
        value !== '' &&
        value !== 'UNKNOWN' &&
        !(Array.isArray(value) && !value.length) &&
        JSON.stringify(value) !== JSON.stringify(next[key])
      );
    })
    .map((key) => labels[key]);
}

export function CompanyPicker({
  value,
  label,
  emptyLabel = 'Nicht zugeordnet',
  onChange,
}: {
  value: string;
  label?: string;
  emptyLabel?: string;
  onChange: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<Company[]>([]);
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(label);
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      void api<{ items: Company[] }>(
        `/api/companies?pageSize=100&search=${encodeURIComponent(search)}`,
        { signal: controller.signal },
      )
        .then((result) => {
          setItems(result.items.filter((company) => company.status !== 'ARCHIVED'));
          setError(undefined);
        })
        .catch((e) => {
          if (!controller.signal.aborted) setError(e);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [search]);
  return (
    <div>
      <label>
        <span className="label">Kunde</span>
        <SearchSelect
          className="input w-full"
          value={value}
          loading={loading}
          onSearchChange={(query) => {
            if (query !== search) {
              setLoading(true);
              setSearch(query);
            }
          }}
          onValueChange={(id) => {
            setSelectedLabel(items.find((item) => item.id === id)?.name);
            onChange(id);
          }}
        >
          <option value="">{emptyLabel}</option>
          {value && !items.some((item) => item.id === value) && (
            <option value={value} hidden={Boolean(search)}>
              {selectedLabel ?? label ?? 'Ausgewählter Kunde'}
            </option>
          )}
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </SearchSelect>
      </label>
      <ErrorMessage error={error} />
    </div>
  );
}

export function DeviceForm({
  device,
  companyId: initialCompanyId,
  onSave,
  onCancel,
  embedded = false,
}: {
  embedded?: boolean;
  device?: DeviceDto;
  companyId?: string;
  onSave: (input: DeviceCreate) => Promise<void>;
  onCancel: () => void;
}) {
  const initialClassification = {
    deviceType: device?.deviceType ?? 'UNKNOWN',
    execution: device?.execution ?? 'UNKNOWN',
    hosting: !device || device.hosting === 'UNKNOWN' ? 'ON_PREMISES' : device.hosting,
    formFactor: device?.formFactor ?? 'UNKNOWN',
    roles: device?.roles ?? [],
    hostingProvider: device?.hostingProvider ?? null,
    hostingRegion: device?.hostingRegion ?? null,
  };
  const [classification, setClassification] = useState(() =>
    normalizeDeviceClassification(initialClassification),
  );
  const [classificationNotice, setClassificationNotice] = useState(() => {
    const fields = classificationResetFields(initialClassification);
    return fields.length
      ? `Bestehende unpassende Angaben werden beim Speichern bereinigt: ${fields.join(', ')}.`
      : '';
  });
  const { deviceType, execution, hosting, formFactor, roles } = classification;
  const rule = deviceClassificationRules[deviceType];
  const options = deviceClassificationOptions(deviceType, execution);
  const changeClassification = (change: Partial<typeof classification>) => {
    const requested = { ...classification, ...change };
    const next = normalizeDeviceClassification(requested);
    setClassification(next);
    const fields = classificationResetFields(requested);
    if (fields.length)
      setClassificationNotice(
        `Zurückgesetzt, damit die Angaben zur Auswahl passen: ${fields.join(', ')}.`,
      );
  };
  const [companyId, setCompanyId] = useState(device?.companyId ?? initialCompanyId ?? '');
  const [siteId, setSiteId] = useState(device?.siteId ?? '');
  const [contactId, setContactId] = useState(device?.responsibleContactId ?? '');
  const [sites, setSites] = useState<Site[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setSites([]);
    setContacts([]);
    if (!companyId) return;
    const controller = new AbortController();
    setLoading(true);
    void Promise.all([
      api<Site[]>(`/api/companies/${companyId}/sites`, { signal: controller.signal }),
      api<Contact[]>(`/api/companies/${companyId}/contacts`, { signal: controller.signal }),
    ])
      .then(([nextSites, nextContacts]) => {
        setSites(nextSites.filter((site) => site.status !== 'ARCHIVED'));
        setContacts(
          nextContacts.filter(
            (contact) =>
              contact.status !== 'ARCHIVED' &&
              (contact.companyId === companyId ||
                contact.memberships?.some(
                  (membership) =>
                    membership.companyId === companyId && membership.status === 'ACTIVE',
                )),
          ),
        );
        setError(undefined);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [companyId]);
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setError(undefined);
    try {
      await onSave({
        name: String(form.get('name')),
        category:
          deviceType === 'UNKNOWN'
            ? (device?.category ?? 'OTHER')
            : rule.category,
        ...classification,
        hostingProvider: classification.hostingProvider?.trim() || null,
        hostingRegion: classification.hostingRegion?.trim() || null,
        status: form.get('status') as DeviceCreate['status'],
        ...Object.fromEntries(
          ['hostname', 'manufacturer', 'model', 'serialNumber', 'assetTag'].map((field) => [
            field,
            String(form.get(field) ?? '').trim() || null,
          ]),
        ),
        companyId: companyId || null,
        siteId: siteId || null,
        responsibleContactId: contactId || null,
      });
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className={embedded ? 'space-y-5' : 'panel mb-5 space-y-5 p-5'}>
      {!embedded && (
        <>
          <h2 className="font-semibold">{device ? 'Gerät bearbeiten' : 'Gerät anlegen'}</h2>
          <p className="text-sm text-muted">
            Geräteidentität und Zuordnung. DeviceDesk ergänzt erkannte Hardwaredaten; manuelle Änderungen bleiben erhalten.
          </p>
        </>
      )}
      <ErrorMessage error={error} />
      <div className="grid gap-4 md:grid-cols-2">
        <label>
          <span className="label">Name *</span>
          <input
            className="input w-full"
            name="name"
            required
            maxLength={200}
            defaultValue={device?.name}
            autoFocus
          />
        </label>
        <label>
          <span className="label">Gerätetyp</span>
          <SearchSelect
            className="input w-full"
            value={deviceType}
            onValueChange={(value) =>
              changeClassification({ deviceType: value as DeviceCreate['deviceType'] })
            }
          >
            {Object.entries(deviceTypes).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </SearchSelect>
        </label>
        <label>
          <span className="label">Bestandsstatus</span>
          <SearchSelect
            className="input w-full"
            name="status"
            defaultValue={device?.status ?? 'ACTIVE'}
          >
            <option value="ACTIVE">Im Bestand</option>
            <option value="INACTIVE">Inaktiv</option>
          </SearchSelect>
        </label>
        {(['hostname', 'manufacturer', 'model', 'serialNumber', 'assetTag'] as const).map(
          (field) => (
            <label key={field}>
              <span className="label">
                {
                  {
                    hostname: 'Hostname',
                    manufacturer: 'Hersteller',
                    model: 'Modell',
                    serialNumber: 'Seriennummer',
                    assetTag: 'Inventarnummer',
                  }[field]
                }
              </span>
              <input
                className="input w-full"
                name={field}
                maxLength={200}
                defaultValue={device?.[field] ?? ''}
              />
            </label>
          ),
        )}
      </div>
      <section className="space-y-4 border-t border-line pt-5">
        <h3 className="font-semibold">Ausführung und Betrieb</h3>
        <p className="text-sm text-muted">
          {rule.virtual
            ? 'Ausführung und Betriebsort bestimmen die weiteren Angaben.'
            : 'Dieser Gerätetyp ist ein physisches Gerät.'}
        </p>
        {classificationNotice && (
          <p role="status" className="text-sm text-muted">
            {classificationNotice}
          </p>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          {rule.virtual && (
            <label>
              <span className="label">Ausführung</span>
              <SearchSelect
                className="input w-full"
                value={execution}
                onValueChange={(value) =>
                  changeClassification({ execution: value as DeviceCreate['execution'] })
                }
              >
                {options.executions.map((key) => (
                  <option key={key} value={key}>
                    {deviceExecutions[key]}
                  </option>
                ))}
              </SearchSelect>
            </label>
          )}
          <label>
            <span className="label">Betriebsort</span>
            <SearchSelect
              className="input w-full"
              value={hosting}
              onValueChange={(value) =>
                changeClassification({ hosting: value as DeviceCreate['hosting'] })
              }
            >
              {options.hostings.map((key) => (
                <option key={key} value={key}>
                  {deviceHostings[key]}
                </option>
              ))}
            </SearchSelect>
          </label>
          {options.forms.length > 1 && (
            <label>
              <span className="label">Bauform</span>
              <SearchSelect
                className="input w-full"
                value={formFactor}
                onValueChange={(value) =>
                  changeClassification({ formFactor: value as DeviceCreate['formFactor'] })
                }
              >
                {options.forms.map((key) => (
                  <option key={key} value={key}>
                    {deviceFormFactors[key]}
                  </option>
                ))}
              </SearchSelect>
            </label>
          )}
          {options.host && (
            <label className="flex items-center gap-2 self-end py-2 text-sm">
              <input
                type="checkbox"
                name="virtualizationHost"
                checked={roles.includes('VIRTUALIZATION_HOST')}
                onChange={(event) =>
                  changeClassification({
                    roles: event.target.checked ? ['VIRTUALIZATION_HOST'] : [],
                  })
                }
              />
              Virtualisierungshost
            </label>
          )}
          {(hosting === 'CLOUD' || hosting === 'HOSTED') &&
            (['hostingProvider', 'hostingRegion'] as const).map((field) => (
              <label key={field}>
                <span className="label">
                  {field === 'hostingProvider' ? 'Anbieter / Betreiber' : 'Region / Rechenzentrum'}
                </span>
                <input
                  className="input w-full"
                  name={field}
                  maxLength={200}
                  value={classification[field] ?? ''}
                  onChange={(event) => changeClassification({ [field]: event.target.value })}
                />
              </label>
            ))}
        </div>
      </section>
      <h3 className="border-t border-line pt-5 font-semibold">Zuordnung</h3>
      <div className="grid gap-4 md:grid-cols-3">
        <CompanyPicker
          value={companyId}
          label={device?.company?.name}
          onChange={(id) => {
            setCompanyId(id);
            setSiteId('');
            setContactId('');
          }}
        />
        <label>
          <span className="label">Standort</span>
          <SearchSelect
            className="input w-full"
            value={siteId}
            disabled={!companyId || loading}
            onValueChange={(value) => setSiteId(value)}
          >
            <option value="">Kein Standort</option>
            {siteId && !sites.some((site) => site.id === siteId) && (
              <option value={siteId}>{device?.site?.name ?? siteId}</option>
            )}
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </SearchSelect>
        </label>
        <label>
          <span className="label">Verantwortlicher Kontakt</span>
          <SearchSelect
            className="input w-full"
            value={contactId}
            disabled={!companyId || loading}
            onValueChange={(value) => setContactId(value)}
          >
            <option value="">Kein Kontakt</option>
            {contactId && !contacts.some((contact) => contact.id === contactId) && (
              <option value={contactId}>
                {device?.responsibleContact?.displayName ?? contactId}
              </option>
            )}
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.displayName}
              </option>
            ))}
          </SearchSelect>
        </label>
      </div>
      <div className="flex gap-2">
        <button className="button" disabled={saving || loading}>
          {saving ? 'Wird gespeichert…' : device ? 'Änderungen speichern' : 'Gerät erstellen'}
        </button>
        <button className="button-secondary" type="button" disabled={saving} onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
